import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DropdownModule } from 'primeng/dropdown';
import { InputTextModule } from 'primeng/inputtext';
import { finalize } from 'rxjs/operators';
import { AnagraficaIndirizziService } from 'src/app/core/anagrafica-indirizzi.service';
import { TIPI_SOGGETTO_OPTIONS } from 'src/app/shared/constants/tipi-soggetto.constants';
import { Indirizzo } from 'src/app/shared/models/indirizzo.model';

@Component({
  selector: 'app-soggetto-dialog',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DialogModule,
    DropdownModule,
    InputTextModule,
    ButtonModule
  ],
  templateUrl: './soggetto-dialog.component.html',
  styleUrls: ['./soggetto-dialog.component.scss']
})
export class SoggettoDialogComponent implements OnInit {
  public visible = false;
  public form: FormGroup;
  public tipiSoggetto = TIPI_SOGGETTO_OPTIONS;
  public saving = false;

  constructor(
    private fb: FormBuilder,
    private anagraficaIndirizziService: AnagraficaIndirizziService,
    private messageService: MessageService
  ) {
    this.form = this.fb.group({
      tipoSoggettoId: [null, Validators.required],
      nome: ['', Validators.required],
      cognome: [''],
      codiceFiscale: [''],
      denominazioneAoo: [''],
      indirizziDigitaliRiferimento: ['']
    });
  }

  ngOnInit(): void {
    this.form.get('tipoSoggettoId')?.valueChanges.subscribe(() => this.applyTipoSoggettoRules());
    this.applyTipoSoggettoRules();
  }

  get nomeLabel(): string {
    switch (this.tipoSoggetto) {
      case 2:
        return 'Denominazione Organizzazione';
      case 3:
        return 'Denominazione Amministrazione / Codice IPA';
      case 4:
        return 'Denominazione Amministrazione';
      default:
        return 'Nome';
    }
  }

  get tipoSoggetto(): number | null {
    return this.form.get('tipoSoggettoId')?.value ?? null;
  }

  get isPersonaFisica(): boolean {
    return this.tipoSoggetto === 1;
  }

  get isPersonaGiuridica(): boolean {
    return this.tipoSoggetto === 2;
  }

  get isPai(): boolean {
    return this.tipoSoggetto === 3;
  }

  get isPae(): boolean {
    return this.tipoSoggetto === 4;
  }

  public open(): void {
    this.visible = true;
    setTimeout(() => {
      const input = document.getElementById('soggetto-nome');
      input?.focus();
    }, 100);
  }

  public close(): void {
    this.visible = false;
    this.resetForm();
  }

  public save(): void {
    if (this.form.invalid || this.saving) {
      return;
    }
    this.saving = true;
    const payload = this.buildPayload();

    this.anagraficaIndirizziService.create(payload as Indirizzo).pipe(
      finalize(() => this.saving = false)
    ).subscribe({
      next: () => {
        this.messageService.add({
          key: 'toast',
          severity: 'success',
          summary: 'Creazione',
          detail: 'Soggetto creato correttamente'
        });
        this.close();
      },
      error: err => {
        this.messageService.add({
          key: 'toast',
          severity: 'error',
          summary: 'Errore',
          detail: err?.error ?? 'Impossibile creare il soggetto'
        });
      }
    });
  }

  public onDialogHide(): void {
    this.resetForm();
  }

  private resetForm(): void {
    this.form.reset();
    this.saving = false;
    this.applyTipoSoggettoRules();
  }

  private buildPayload(): Partial<Indirizzo> {
    const value = this.form.value;
    return {
      nomeIndirizzo: value.nome?.trim(),
      tipoSoggettoId: value.tipoSoggettoId,
      cognome: this.isPersonaFisica ? value.cognome?.trim() ?? null : null,
      codiceFiscale: value.codiceFiscale?.trim() ?? null,
      denominazioneAoo: this.isPai ? value.denominazioneAoo?.trim() ?? null : null,
      indirizziDigitaliRiferimento: this.isPae ? value.indirizziDigitaliRiferimento?.trim() ?? null : null
    };
  }

  private applyTipoSoggettoRules(): void {
    this.toggleValidators('cognome', this.isPersonaFisica);
    this.toggleValidators('codiceFiscale', false);
    this.toggleValidators('denominazioneAoo', this.isPai);
    this.toggleValidators('indirizziDigitaliRiferimento', this.isPae);

    if (!this.isPersonaFisica) {
      this.form.patchValue({ cognome: null }, { emitEvent: false });
    }
    if (!this.isPai) {
      this.form.patchValue({ denominazioneAoo: null }, { emitEvent: false });
    }
    if (!this.isPae) {
      this.form.patchValue({ indirizziDigitaliRiferimento: null }, { emitEvent: false });
    }
  }

  private toggleValidators(controlName: string, required: boolean): void {
    const control = this.form.get(controlName);
    if (!control) {
      return;
    }
    control.setValidators(required ? [Validators.required] : []);
    control.updateValueAndValidity({ emitEvent: false });
  }
}
